import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { URI_INTERNAL } from '../utils/pathLocations.js';
import {
    ASSET_API,
    DEPARTMENT_API,
    INVENTORY_SESSION_API,
    QUALITYLEVEL_API,
    CONNECTIONTYPE_API,
    ASSETSTATUS_API,
    USAGEPURPOSE_API,
    EQUIPMENTTYPE_API,
    EQUIPMENTSOURCE_API,
    FUNDINGSOURCE_API,
    ASSETGROUP_API,
} from '../utils/apiUrl.js';
import Select from 'react-select';
import { Get } from '../utils/httpProvider';
import {
    PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const STATUS_COLORS_DEFAULT = ['#4361EE', '#00B4D8', '#2DC653', '#F7B731', '#EF476F', '#9B5DE5', '#F77F00'];

const STATUS_COLOR_MAP = {
    'Chờ phê duyệt': '#F7B731', // Yellow
    'Sẵn sàng': '#2DC653',      // Green
    'Đang sửa chữa': '#EF476F',  // Red
    'Điều chuyển': '#00B4D8',   // Blue/Cyan
};

const getStatusColor = (name, index, backendColor) => {
    if (backendColor) return backendColor;
    if (name && STATUS_COLOR_MAP[name]) return STATUS_COLOR_MAP[name];
    if (name) {
        // Try fuzzy match
        const foundKey = Object.keys(STATUS_COLOR_MAP).find(k => name.includes(k));
        if (foundKey) return STATUS_COLOR_MAP[foundKey];
    }
    return STATUS_COLORS_DEFAULT[index % STATUS_COLORS_DEFAULT.length];
};

const normalizeData = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    return raw.data || raw.Data || [];
};

const selectStyles = {
    control: (base) => ({
        ...base,
        borderRadius: '8px',
        borderColor: '#e0e0e0',
        minHeight: '42px',
        '&:hover': { borderColor: '#4361EE' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// Custom Pie label
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="bold">
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// Custom Tooltip for Bar charts
const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#fff', border: '1px solid #eee', padding: '8px 14px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}>
            <p style={{ fontWeight: 700, margin: '0 0 4px' }}>{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ margin: '2px 0', color: entry.color }}>
                    {entry.name}: <b>{entry.value}</b>
                </p>
            ))}
        </div>
    );
};

const PieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value, payload: innerPayload } = payload[0];
    return (
        <div style={{ background: '#fff', border: '1px solid #eee', padding: '8px 14px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13 }}>
            <p style={{ fontWeight: 700, margin: '0 0 4px', color: innerPayload.color }}>{name}</p>
            <p style={{ margin: 0 }}>Số lượng: <b>{value}</b></p>
        </div>
    );
};

const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: 'none',
    overflow: 'hidden',
};

const Dashboard = () => {
    const [filters, setFilters] = useState({
        departmentId: null,
        assetStatusId: null,
        usagePurposeId: null,
        equipmentTypeId: null,
        equipmentSourceId: null,
        qualityLevelId: null,
        fundingSourceId: null,
        connectionTypeId: null,
        assetGroupId: null,
        isDisposed: false
    });

    const buildQueryString = (f) => {
        return Object.entries(f)
            .filter(([k, v]) => v !== null && v !== '' && v !== false) // Only include truthy values (keep boolean true)
            .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
            .join('&');
    };

    const queryString = buildQueryString(filters);
    const qsPrefix = queryString ? `?${queryString}` : '';

    // ── Queries Lọc theo khoa phòng ──────────────────────────────────────────────────────────────
    const { data: deptRaw, isLoading: deptLoading } = useQuery(
        ['getDeptStats', filters], 
        () => Get(`${ASSET_API.GET_DEPT_STATS}${qsPrefix}`)
    );
    const deptList   = normalizeData(deptRaw);

    const { data: deptsRaw } = useQuery(['getAllDeptsDashboard'], () => Get(DEPARTMENT_API.GET_ALL));
    const departmentOptions = useMemo(() => {
        const list = normalizeData(deptsRaw);
        return [{ value: null, label: 'Toàn bệnh viện' }, ...list.map(d => {
            const countInfo = deptList.find(x => x.Label === d.departmentName || x.label === d.departmentName);
            const count = countInfo ? (countInfo.Value || countInfo.value || 0) : 0;
            return { value: d.id, label: `${d.departmentName} (${count} máy)` };
        })];
    }, [deptsRaw, deptList]);

    // ── Load options cho 7 danh mục ─────────────────────────────────────────────────────────────
    const getOptions = (raw, labelField, valueField = 'Id', allLabel = 'Tất cả') => {
        const list = normalizeData(raw);
        return [{ value: null, label: allLabel }, ...list.map(d => ({ value: d[valueField] || d.id, label: d[labelField] || d[labelField.charAt(0).toLowerCase() + labelField.slice(1)] }))];
    };

    const { data: astRaw } = useQuery(['getAssetStatusesDashboard'], () => Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const assetStatusOptions = useMemo(() => getOptions(astRaw, 'StatusName', 'Id', 'Tất cả trạng thái'), [astRaw]);

    const { data: agRaw } = useQuery(['getAssetGroupsDashboard'], () => Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const assetGroupOptions = useMemo(() => getOptions(agRaw, 'AssetGroupName', 'Id', 'Tất cả nhóm TS'), [agRaw]);

    const { data: upRaw } = useQuery(['getUsagePurposesDashboard'], () => Get(USAGEPURPOSE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const usagePurposeOptions = useMemo(() => getOptions(upRaw, 'UsagePurposeName', 'Id', 'Tất cả mục đích SD'), [upRaw]);

    const { data: etRaw } = useQuery(['getEquipmentTypesDashboard'], () => Get(EQUIPMENTTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const equipmentTypeOptions = useMemo(() => getOptions(etRaw, 'EquipmentTypeName', 'Id', 'Tất cả loại trang bị'), [etRaw]);

    const { data: esRaw } = useQuery(['getEquipmentSourcesDashboard'], () => Get(EQUIPMENTSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const equipmentSourceOptions = useMemo(() => getOptions(esRaw, 'EquipmentSourceName', 'Id', 'Tất cả nguồn TB'), [esRaw]);

    const { data: fsRaw } = useQuery(['getFundingSourcesDashboard'], () => Get(FUNDINGSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const fundingSourceOptions = useMemo(() => getOptions(fsRaw, 'FundingSourceName', 'Id', 'Tất cả nguồn kinh phí'), [fsRaw]);

    const { data: qlRaw } = useQuery(['getQualityLevelsDashboard'], () => Get(QUALITYLEVEL_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const qualityLevelOptions = useMemo(() => getOptions(qlRaw, 'QualityLevelName', 'Id', 'Tất cả cấp chất lượng'), [qlRaw]);

    const { data: ctRaw } = useQuery(['getConnectionTypesDashboard'], () => Get(CONNECTIONTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 }));
    const connectionTypeOptions = useMemo(() => getOptions(ctRaw, 'ConnectionTypeName', 'Id', 'Tất cả loại kết nối'), [ctRaw]);

    // ── Load biểu đồ thống kê ────────────────────────────────────────────────────────────────
    const { data: statusRaw, isLoading: statusLoading } = useQuery(
        ['getStatusStats', filters],
        () => Get(`${ASSET_API.GET_STATUS_STATS}${qsPrefix}`)
    );
    
    const { data: groupRaw, isLoading: groupLoading } = useQuery(
        ['getGroupStats', filters],
        () => Get(`${ASSET_API.GET_GROUP_STATS}${qsPrefix}`)
    );

    const statusList = normalizeData(statusRaw);
    const groupList  = normalizeData(groupRaw);

    // ── Queries Inventory ────────────────────────────────────────────────────
    const { data: inventoryData } = useQuery(['getInventoryPaging', filters.departmentId], () => 
        Get(INVENTORY_SESSION_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1 })
    );

    // ── Status Pie Data ──────────────────────────────────────────────────────
    const statusPieData = useMemo(() =>
        statusList.map((x, i) => {
            const name = x.label || x.Label;
            return {
                name: name,
                value: x.value || x.Value,
                color: getStatusColor(name, i, x.color || x.Color),
            };
        }),
    [statusList]);

    // ── Calculate Dashboard Cards Data ───────────────────────────────────────
    const totalAssets = useMemo(() => statusPieData.reduce((sum, item) => sum + (item.value || 0), 0), [statusPieData]);
    const repairingAssets = useMemo(() => statusPieData.find(x => x.name?.toLowerCase().includes('sửa chữa'))?.value || 0, [statusPieData]);
    const transferringAssets = useMemo(() => statusPieData.find(x => x.name?.toLowerCase().includes('điều chuyển'))?.value || 0, [statusPieData]);
    const inventoryingAssets = inventoryData?.totalRecords || inventoryData?.TotalRecords || 0;

    // ── Dept Bar Data ────────────────────────────────────────────────────────
    const deptBarData = useMemo(() =>
        deptList.map(x => ({ name: x.label || x.Label, value: x.value || x.Value })),
    [deptList]);

    // ── Group Stacked Bar ─────────────────────────────────────────────────────
    const { groupBarData, statuses } = useMemo(() => {
        if (!groupList.length) return { groupBarData: [], statuses: [] };

        const statusSet = new Map(); // statusName -> color
        const groupMap = {};

        groupList.forEach(x => {
            const gName = x.groupName || x.GroupName;
            const sName = x.statusName || x.StatusName;
            const count  = x.count || x.Count;
            const color  = x.statusColor || x.StatusColor;

            if (!groupMap[gName]) groupMap[gName] = { name: gName };
            groupMap[gName][sName] = (groupMap[gName][sName] || 0) + count;
            if (!statusSet.has(sName)) statusSet.set(sName, color);
        });

        return {
            groupBarData: Object.values(groupMap),
            statuses: [...statusSet.entries()].map(([name, color], i) => ({
                name,
                color: getStatusColor(name, i, color),
            })),
        };
    }, [groupList]);

    const deptChartHeight = Math.max(450, deptList.length * 35);

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="mb-4">
                <h3 style={{ fontWeight: 700, color: '#1a1a1a' }}>Tổng quan tài sản</h3>
                <p className="text-muted mb-0">Hệ thống quản lý trang thiết bị vật tư</p>
            </div>

            {/* 4 Small Boxes */}
            <Row className="mb-4">
                <Col lg={3} md={6} sm={12} className="mb-3 mb-lg-0">
                    <div style={{ background: '#17a2b8', borderRadius: '0.25rem', padding: '20px', position: 'relative', color: '#fff', overflow: 'hidden', boxShadow: '0 0 1px rgba(0,0,0,.125), 0 1px 3px rgba(0,0,0,.2)' }}>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <h3 style={{ fontSize: '2.2rem', fontWeight: 'bold', margin: '0 0 10px 0' }}>{totalAssets}</h3>
                            <p style={{ fontSize: '1rem', margin: 0 }}>Số lượng tài sản</p>
                        </div>
                        <div style={{ position: 'absolute', right: '15px', top: '15px', zIndex: 1, color: 'rgba(0,0,0,0.15)', fontSize: '70px' }}>
                            <i className="fas fa-boxes"></i>
                        </div>
                        <Link to={URI_INTERNAL.ASSET_PATH || '/'} style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.1)', padding: '3px 0', textAlign: 'center', color: '#fff', textDecoration: 'none', zIndex: 3, fontSize: '0.8rem', display: 'block' }}>
                            More info <i className="fas fa-arrow-circle-right ml-1"></i>
                        </Link>
                    </div>
                </Col>
                <Col lg={3} md={6} sm={12} className="mb-3 mb-lg-0">
                    <div style={{ background: '#28a745', borderRadius: '0.25rem', padding: '20px', position: 'relative', color: '#fff', overflow: 'hidden', boxShadow: '0 0 1px rgba(0,0,0,.125), 0 1px 3px rgba(0,0,0,.2)' }}>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <h3 style={{ fontSize: '2.2rem', fontWeight: 'bold', margin: '0 0 10px 0' }}>{repairingAssets}</h3>
                            <p style={{ fontSize: '1rem', margin: 0 }}>Tài sản đang sửa chữa</p>
                        </div>
                        <div style={{ position: 'absolute', right: '15px', top: '15px', zIndex: 1, color: 'rgba(0,0,0,0.15)', fontSize: '70px' }}>
                            <i className="fas fa-tools"></i>
                        </div>
                        <Link to={'/phieu-sua-chua'} style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.1)', padding: '3px 0', textAlign: 'center', color: '#fff', textDecoration: 'none', zIndex: 3, fontSize: '0.8rem', display: 'block' }}>
                            More info <i className="fas fa-arrow-circle-right ml-1"></i>
                        </Link>
                    </div>
                </Col>
                <Col lg={3} md={6} sm={12} className="mb-3 mb-lg-0">
                    <div style={{ background: '#ffc107', borderRadius: '0.25rem', padding: '20px', position: 'relative', color: '#fff', overflow: 'hidden', boxShadow: '0 0 1px rgba(0,0,0,.125), 0 1px 3px rgba(0,0,0,.2)' }}>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <h3 style={{ fontSize: '2.2rem', fontWeight: 'bold', margin: '0 0 10px 0', color: '#1f2d3d' }}>{transferringAssets}</h3>
                            <p style={{ fontSize: '1rem', margin: 0, color: '#1f2d3d' }}>Đang được điều chuyển</p>
                        </div>
                        <div style={{ position: 'absolute', right: '15px', top: '15px', zIndex: 1, color: 'rgba(0,0,0,0.15)', fontSize: '70px' }}>
                            <i className="fas fa-exchange-alt"></i>
                        </div>
                        <Link to={'/asset-transfer-list'} style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.1)', padding: '3px 0', textAlign: 'center', color: '#1f2d3d', textDecoration: 'none', zIndex: 3, fontSize: '0.8rem', display: 'block' }}>
                            More info <i className="fas fa-arrow-circle-right ml-1"></i>
                        </Link>
                    </div>
                </Col>
                <Col lg={3} md={6} sm={12} className="mb-3 mb-lg-0">
                    <div style={{ background: '#dc3545', borderRadius: '0.25rem', padding: '20px', position: 'relative', color: '#fff', overflow: 'hidden', boxShadow: '0 0 1px rgba(0,0,0,.125), 0 1px 3px rgba(0,0,0,.2)' }}>
                        <div style={{ position: 'relative', zIndex: 2 }}>
                            <h3 style={{ fontSize: '2.2rem', fontWeight: 'bold', margin: '0 0 10px 0' }}>{inventoryingAssets}</h3>
                            <p style={{ fontSize: '1rem', margin: 0 }}>Đang được kiểm kê</p>
                        </div>
                        <div style={{ position: 'absolute', right: '15px', top: '15px', zIndex: 1, color: 'rgba(0,0,0,0.15)', fontSize: '70px' }}>
                            <i className="fas fa-clipboard-check"></i>
                        </div>
                        <Link to={'/kiem-ke-tai-san'} style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.1)', padding: '3px 0', textAlign: 'center', color: '#fff', textDecoration: 'none', zIndex: 3, fontSize: '0.8rem', display: 'block' }}>
                            More info <i className="fas fa-arrow-circle-right ml-1"></i>
                        </Link>
                    </div>
                </Col>
            </Row>

            {/* Filter */}
            <div style={{ ...cardStyle, padding: '16px 20px', marginBottom: 24 }}>
                <Row className="align-items-center mb-3">
                    <Col md={12}>
                        <span className="font-weight-bold text-secondary" style={{ fontSize: '1.1rem' }}>
                            <i className="fas fa-filter mr-2"></i> Bộ lọc toàn cục:
                        </span>
                        <small className="text-muted ml-2">(áp dụng cho tất cả Biểu đồ & Thống kê)</small>
                    </Col>
                </Row>
                <Row>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Khoa / Phòng</label>
                        <Select
                            styles={selectStyles}
                            options={departmentOptions}
                            value={departmentOptions.find(o => o.value === filters.departmentId) || departmentOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, departmentId: opt?.value ?? null }))}
                            placeholder="Chọn Khoa / Phòng..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Nhóm TS</label>
                        <Select
                            styles={selectStyles}
                            options={assetGroupOptions}
                            value={assetGroupOptions.find(o => o.value === filters.assetGroupId) || assetGroupOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, assetGroupId: opt?.value ?? null }))}
                            placeholder="Chọn Nhóm TS..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Trạng thái HĐ</label>
                        <Select
                            styles={selectStyles}
                            options={assetStatusOptions}
                            value={assetStatusOptions.find(o => o.value === filters.assetStatusId) || assetStatusOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, assetStatusId: opt?.value ?? null }))}
                            placeholder="Chọn Trạng thái..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Mục đích SD</label>
                        <Select
                            styles={selectStyles}
                            options={usagePurposeOptions}
                            value={usagePurposeOptions.find(o => o.value === filters.usagePurposeId) || usagePurposeOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, usagePurposeId: opt?.value ?? null }))}
                            placeholder="Chọn Mục đích..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Loại trang bị</label>
                        <Select
                            styles={selectStyles}
                            options={equipmentTypeOptions}
                            value={equipmentTypeOptions.find(o => o.value === filters.equipmentTypeId) || equipmentTypeOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, equipmentTypeId: opt?.value ?? null }))}
                            placeholder="Chọn Loại TB..."
                            menuPortalTarget={document.body}
                        />
                    </Col>

                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Nguồn trang bị</label>
                        <Select
                            styles={selectStyles}
                            options={equipmentSourceOptions}
                            value={equipmentSourceOptions.find(o => o.value === filters.equipmentSourceId) || equipmentSourceOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, equipmentSourceId: opt?.value ?? null }))}
                            placeholder="Chọn Nguồn TB..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Nguồn kinh phí</label>
                        <Select
                            styles={selectStyles}
                            options={fundingSourceOptions}
                            value={fundingSourceOptions.find(o => o.value === filters.fundingSourceId) || fundingSourceOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, fundingSourceId: opt?.value ?? null }))}
                            placeholder="Chọn Kinh phí..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Cấp chất lượng</label>
                        <Select
                            styles={selectStyles}
                            options={qualityLevelOptions}
                            value={qualityLevelOptions.find(o => o.value === filters.qualityLevelId) || qualityLevelOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, qualityLevelId: opt?.value ?? null }))}
                            placeholder="Chọn Chất lượng..."
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={3} className="mb-3">
                        <label className="font-weight-bold text-muted small mb-1">Loại kết nối</label>
                        <Select
                            styles={selectStyles}
                            options={connectionTypeOptions}
                            value={connectionTypeOptions.find(o => o.value === filters.connectionTypeId) || connectionTypeOptions[0]}
                            onChange={opt => setFilters(prev => ({ ...prev, connectionTypeId: opt?.value ?? null }))}
                            placeholder="Chọn Kết nối..."
                            menuPortalTarget={document.body}
                        />
                    </Col>

                    {/* <Col md={12} className="mt-2">
                        <div className="custom-control custom-checkbox">
                            <input 
                                type="checkbox" 
                                className="custom-control-input" 
                                id="isDisposedFilter" 
                                checked={filters.isDisposed}
                                onChange={e => setFilters(prev => ({ ...prev, isDisposed: e.target.checked }))}
                            />
                            <label className="custom-control-label text-muted" htmlFor="isDisposedFilter">
                                Đánh dấu bao gồm tài sản đã được thanh lý
                            </label>
                        </div>
                    </Col> */}
                </Row>
            </div>

            <Row>
                {/* ── Pie: Tình trạng tài sản ── */}
                <Col lg={5} className="mb-4">
                    <div style={{ ...cardStyle, height: '100%' }}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">Tình trạng tài sản</div>
                        <div style={{ padding: '16px 8px', height: 400 }}>
                            {statusLoading ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                            ) : !statusPieData.length ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={statusPieData}
                                            cx="50%" cy="45%"
                                            innerRadius="45%"
                                            outerRadius="70%"
                                            dataKey="value"
                                            nameKey="name"
                                            labelLine={false}
                                            label={renderPieLabel}
                                            isAnimationActive={true}
                                            animationBegin={100}
                                            animationDuration={900}
                                        >
                                            {statusPieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} stroke="#fff" strokeWidth={2} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<PieTooltip />} />
                                        <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12, paddingTop: 20 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </Col>

                {/* ── Stacked Bar: Nhóm tài sản ── */}
                <Col lg={7} className="mb-4">
                    <div style={{ ...cardStyle, height: '100%' }}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">Phân bổ theo Nhóm tài sản</div>
                        <div style={{ padding: '16px 8px', height: 400 }}>
                            {groupLoading ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                            ) : !groupBarData.length ? (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={groupBarData} margin={{ top: 10, right: 20, left: 0, bottom: 80 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} height={80} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<BarTooltip />} />
                                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 20 }} />
                                        {statuses.map(s => (
                                            <Bar
                                                key={s.name}
                                                dataKey={s.name}
                                                stackId="a"
                                                fill={s.color}
                                                isAnimationActive={true}
                                                animationBegin={200}
                                                animationDuration={1000}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </Col>

                {/* ── Horizontal Bar: Khoa phòng ── */}
                <Col lg={12} className="mb-4">
                    <div style={cardStyle}>
                        <div className="px-4 py-3 border-bottom font-weight-bold">
                            Phân bổ theo khoa phòng
                            <span className="text-muted font-weight-normal ml-2" style={{ fontSize: 13 }}>(cuộn để xem thêm)</span>
                        </div>
                        {/* Scrollable wrapper */}
                        <div style={{ overflowX: 'auto' }}>
                            <div style={{ width: `${Math.max(900, deptList.length * 60)}px`, height: 340, padding: '16px 8px' }}>
                                {deptLoading ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">Đang tải...</div>
                                ) : !deptBarData.length ? (
                                    <div className="h-100 d-flex align-items-center justify-content-center text-muted">Không có dữ liệu</div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={deptBarData} margin={{ top: 10, right: 20, left: 0, bottom: 100 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" interval={0} height={100} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                            <Tooltip content={<BarTooltip />} />
                                            <Bar
                                                dataKey="value"
                                                name="Số tài sản"
                                                fill="#4361EE"
                                                radius={[4, 4, 0, 0]}
                                                isAnimationActive={true}
                                                animationBegin={300}
                                                animationDuration={1000}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
